import _isEqual from 'lodash.isequal'
import { toTitleCase } from '../../utils'
import { FulfillmentStatus, IOrdersMetaState, OrderItemCount, OrderStatus, TimeFrameDimension } from './types'
import config from '../../config'
import { ConnectedMarket } from '../../current/types'

/**
 * Turns PAID,UNPAID into Paid, Unpaid
 */
export function stringListToTitleCase(str: string, prefixText?: string): string {
  return str
    .split(',')
    .filter(k => k)
    .map(k => [prefixText, toTitleCase(k)].filter(f => f).join(' '))
    .sort((a, b) => a.localeCompare(b))
    .join(', ')
}

export function humanReadablePickAndPackStatusList(statusList: string): string {
  const joinedResult = stringListToTitleCase(
    statusList
      .split(',')
      .map(s => {
        switch (s) {
          case 'PICK_LIST_ELIGIBLE':
            return 'Not started'
          case 'PICK_IN_PROGRESS':
            return 'In progress'
          default:
            return s
        }
      })
      .join(',')
  )
  return `Pick and pack status: ${joinedResult}`
}

// Filter out all keys with null values
export const removeNulls = obj => {
  const output = {}

  Object.keys(obj).forEach(oo => {
    if (obj[oo] !== null) {
      output[oo] = obj[oo]
    }
  })
  return output
}

// Filter out all keys with empty strings (empty strings means they're set to off)
const removeEmptyStrings = obj => {
  const output = {}
  Object.keys(obj).forEach(oo => {
    if (obj[oo] !== '') {
      output[oo] = obj[oo]
    }
  })
  return output
}

export const BLACK_LIST_META: string[] = [
  'page',
  'perPage',
  'total',
  'totalStatus',
  'maxPages',
  'isNext',
  'cachedResultCount',
  'sort',
  'orderedAt',
  'query',
  'inventoryLocationNames',
  'resourceFilterId'
]
// Sort alphabetically a comma separated string
export const sortCommaSeparatedString = (str: string) => {
  const sortedString = str.split(',').sort().join(',')
  return sortedString
}

export const shallowCompareMeta = (obj1: IOrdersMetaState, obj2: IOrdersMetaState) => {
  // When comparing meta with filters remove nulls and remove strings, both indicate the filter is off
  const copy1 = removeEmptyStrings(removeNulls({ ...obj1 }))
  const copy2 = removeEmptyStrings(removeNulls({ ...obj2 }))
  BLACK_LIST_META.forEach((l: string) => {
    delete copy1[l]
    delete copy2[l]
  })
  Object.keys(copy1).forEach(x => {
    if (typeof copy1[x] === 'string') {
      copy1[x] = sortCommaSeparatedString(copy1[x])
    }
  })
  Object.keys(copy2).forEach(x => {
    if (typeof copy2[x] === 'string') {
      copy2[x] = sortCommaSeparatedString(copy2[x])
    }
  })
  return _isEqual(copy1, copy2)
}

const marketAccountIdsForMarkets = (marketIds: string[], connectedMarkets: ConnectedMarket[]): number[] => {
  const markets = connectedMarkets.filter(m => marketIds.includes(m.marketId.toString()))

  // maintain marketAccountIds in sorted order (a poor man's Set) so that filters are equivalent regardless of the order you select each account.
  // If/when someone eventually wants these to be sorted in some particular order (maybe by the MarketAccount.label?), we can join in connectedMarkets.
  return markets.map(m => m.marketAccountId).sort()
}

// Migrate resource filters client-side instead of backfilling old data.
// Migrating the resource filters at fetch time allows us to compare them successfully (specifically the marketAccountIds)
// with the meta we get when fetching orders or toggling filters in the UI.
export const migrateMarketIdToMarketAccountIds = (filters: any, connectedMarkets: ConnectedMarket[]) => {
  const extras: any = {
    marketAccountIds: filters.marketAccountIds?.split(',') || [], // note, this starts as an array. Will be converted to a CSV string at the end.
    marketIds: [] // same ^
  }

  // There are saved order ResourceFilters with ebay in the marketIds, and we have now started searching orders by marketAccountId.
  // Any other market that gets upgraded to support multi-account (hello shopify!) will need this.
  // Note, keep in sync with marketIdToFilterField
  const marketsThatNeedMigrating = [config.MARKETS.ebay]

  filters.marketIds.split(',').forEach((marketId: string) => {
    const correspondingMarketAccountIds = marketAccountIdsForMarkets([marketId], connectedMarkets)
    if (marketsThatNeedMigrating.includes(parseInt(marketId)) && correspondingMarketAccountIds.length > 0) {
      extras.marketAccountIds = extras.marketAccountIds.concat(correspondingMarketAccountIds)
    } else {
      extras.marketIds = extras.marketIds.concat(marketId)
    }
  })

  extras.marketAccountIds = extras.marketAccountIds.join(',')
  extras.marketIds = extras.marketIds.join(',')

  return {
    ...filters,
    ...extras
  }
}

export const dateInPast = comparedDate => {
  if (comparedDate) {
    const today = new Date()
    if (new Date(comparedDate).getTime() <= today.getTime()) {
      return true
    }
  }

  return false
}

export const showUnlockForPickingButton = (order, user, picklistFeatureFlag) => {
  if (!picklistFeatureFlag) return false

  if (order.fulfillmentStatus === FulfillmentStatus.FULFILLED) {
    return false
  }

  if (!order.lockedForPickingUntil) return false
  if (order.lockedForPickingUntil && dateInPast(order.lockedForPickingUntil)) return false

  return true
}

/** Default ordered_at window for presets that would otherwise scan the whole org. */
export const defaultOrdersTimeFrame = (days: number): [Date, Date] => {
  const end = new Date()
  end.setHours(23, 59, 59, 999)
  const start = new Date(end)
  start.setDate(start.getDate() - days)
  start.setHours(0, 0, 0, 0)
  return [start, end]
}

/** True when the query would match OrdersFinder search (non-empty tsquery). */
export const meaningfulOrdersSearchQuery = (query: string): boolean => {
  return Boolean(query?.trim()) && query.trim() !== '.' && !/^[( *& %$#';\s]+$/.test(query.trim())
}

/** Mirrors OrdersFinder.restrictive_filter_present? for client-side guardrails. */
export const ordersIndexHasRestrictiveFilter = (meta: IOrdersMetaState): boolean => {
  if (meaningfulOrdersSearchQuery(meta.query || '')) return true
  if (meta.marketIds || meta.marketAccountIds) return true
  if (meta.inventoryLocationIds) return true
  if (meta.categoryIds) return true
  if (meta.shippingMethod) return true
  if (meta.shippingDestination) return true
  if (meta.fulfillmentStatus) return true
  if (meta.paymentStatus) return true
  if (meta.pickAndPackStatus) return true
  if (meta.includeOverdue || meta.includeUrgent) return true
  if (meta.orderItemsCount === OrderItemCount.SINGLE_ITEMS || meta.orderItemsCount === OrderItemCount.MULTI_ITEMS) {
    return true
  }
  if (
    meta.status === OrderStatus.OPEN ||
    meta.status === OrderStatus.ARCHIVED ||
    meta.status === OrderStatus.CANCELLED
  ) {
    return true
  }
  // time_frame only restricts when paired with a dimension the API applies
  const hasTimeFrame = (meta.timeFrame || []).filter(Boolean).length >= 2
  const hasTimeFrameDimension =
    meta.timeFrameDimension === TimeFrameDimension.ORDERED_AT ||
    meta.timeFrameDimension === TimeFrameDimension.PAID_AT ||
    meta.timeFrameDimension === TimeFrameDimension.SHIP_BY_DATE
  return hasTimeFrame && hasTimeFrameDimension
}
